import { slideInFromBottom, staggerContainer, staggerItem, viewportOptions } from '@/lib/animations';
import { cn } from '@/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Clock, CreditCard, DollarSign, Info, MessageSquare, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { MotionButton } from '../ui/motion-button';

interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
  icon: React.ElementType;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}

const FAQItem = ({ question, answer, icon: Icon, isOpen, onToggle }: FAQItemProps) => {
  return (
    <motion.div
      className="overflow-hidden border-b border-gray-200 last:border-0"
      variants={staggerItem}
    >
      <motion.button
        onClick={onToggle}
        className="w-full py-6 px-8 flex items-center justify-between text-left transition-colors hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
        aria-expanded={isOpen}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <div className="flex items-center gap-4">
          <div className={cn(
            "p-2.5 rounded-lg shrink-0 transition-colors duration-200",
            isOpen ? "bg-brand/10 text-brand" : "bg-gray-100 text-gray-500"
          )}>
            <Icon className="h-5 w-5" />
          </div>
          <h3 className={cn(
            "text-xl font-medium transition-colors duration-200",
            isOpen ? "text-brand" : "text-black"
          )}>
            {question}
          </h3>
        </div>
        <div
          className={`shrink-0 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'
            }`}
        >
          <ChevronDown className="h-5 w-5" />
        </div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-8 pb-6 pt-2 text-gray-600 pl-20">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default function FAQ() {
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);

  const toggleItem = (question: string) => {
    setOpenQuestion(openQuestion === question ? null : question);
  };

  const faqItems = [
    {
      icon: ShieldCheck,
      question: "What advantages of selling with Swim over using a traditional real estate broker?",
      answer: (
        <div className="space-y-3">
          <p>Our technology-driven approach saves you money while providing all the expertise you need:</p>
          <ul className="list-disc pl-5 space-y-3">
            <li>
              <strong>Save on commission</strong>
              <p className="mt-1">Turn excessive fees into retirement savings</p>
            </li>
            <li>
              <strong>Step-by-step guidance</strong>
              <p className="mt-1">Clear processes and AI support at every stage</p>
            </li>
            <li>
              <strong>Maximum exposure</strong>
              <p className="mt-1">Same listing reach as traditional brokerages</p>
            </li>
            <li>
              <strong>You stay in control</strong>
              <p className="mt-1">Sell on your terms, we just help you get it done</p>
            </li>
          </ul>
        </div>
      )
    },
    {
      icon: DollarSign,
      question: "Are your fees really lower, or are there hidden costs?",
      answer: (
        <div className="space-y-3">
          <p>Simple pricing is something we really believe in. Our pricing includes everything you need to get your home up and listed on the MLS.</p>
          <p>Here's what's included in our fee:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Full MLS and REALTOR.ca listing exposure</li>
            <li>Professional listing creation and management</li>
            <li>Access to all Swim platform tools and resources</li>
            <li>Fixed listing fee instead of percentage-based commissions</li>
          </ul>
          <p>We don't currently cover closing costs or legal costs, though we have recommendations on providers you can use.</p>
        </div>
      )
    },
    {
      icon: CreditCard,
      question: "When do I pay for your services?",
      answer: (
        <p>Our service requires payment upfront, before your home is listed. Once payment is completed, we'll proceed with preparing and publishing your listing to ensure maximum exposure on MLS, REALTOR.ca, and other platforms.</p>
      )
    },
    {
      icon: Clock,
      question: "How long does it take to get listed?",
      answer: (
        <div className="space-y-3">
          <p>Getting your property listed with Swim is a quick and efficient process:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>It takes just <strong>10 minutes</strong> to submit all your property details through our streamlined platform</li>
            <li>Once we have your complete information, your property will be listed on MLS, REALTOR.ca, and other major platforms within <strong>48 hours</strong></li>
            <li>Our team reviews your submission to ensure everything is optimized for maximum visibility</li>
            <li>You'll receive confirmation as soon as your listing goes live</li>
          </ul>
          <p>Our efficient process means you can start receiving interest from potential buyers in just a matter of days, not weeks.</p>
        </div>
      )
    },
    {
      icon: Info,
      question: "How does your service comply with real estate regulations and laws?",
      answer: (
        <div className="space-y-3">
          <p>We take regulatory compliance very seriously and have built our service to fully adhere to all real estate laws:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>We partner with licensed, registered brokerages in each jurisdiction we operate in</li>
            <li>We use the same standard forms and contracts that traditional agents use, ensuring legal compliance</li>
            <li>All listings meet MLS and REALTOR.ca requirements</li>
            <li>Our documentation and processes follow all provincial and local regulations</li>
            <li>We adhere to all disclosure requirements in each market</li>
          </ul>
          <p>This approach ensures that you get the same compliance as with traditional brokerages, but with our more efficient and cost-effective service model.</p>
        </div>
      )
    }
  ];

  return (
    <section>
      <motion.div
        className="max-w-3xl mx-auto text-center mb-16"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOptions}
      >
        <motion.h2 className="landing-title" variants={staggerItem}>
          Frequently Asked Questions
        </motion.h2>
        <motion.p className="landing-subtitle" variants={staggerItem}>
          Everything you need to know about selling with Swim
        </motion.p>
      </motion.div>

      <motion.div
        className="bg-white/95 backdrop-blur-sm rounded-xl shadow-md overflow-hidden max-w-4xl mx-auto border border-white/20"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOptions}
      >
        {faqItems.map((item, index) => (
          <FAQItem
            key={item.question}
            question={item.question}
            answer={item.answer}
            icon={item.icon}
            isOpen={openQuestion === item.question}
            onToggle={() => toggleItem(item.question)}
            index={index}
          />
        ))}
      </motion.div>

      <motion.div
        className="mt-12 text-center"
        variants={slideInFromBottom}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOptions}
      >
        <p className="text-gray-700 mb-4">Still have questions?</p>
        <MotionButton asChild variant="default" className="rounded-full px-6 py-2 h-auto">
          <a href="mailto:support@swimhomes.ca" className="flex items-center gap-2">
            Contact us <MessageSquare className="h-4 w-4" />
          </a>
        </MotionButton>
      </motion.div>
    </section>
  );
}